import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import style from './MoreButton.module.scss';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';

const MoreButtonEventBus = {
  listeners: [],
  subscribe(cb) {
    this.listeners.push(cb);
    return () => (this.listeners = this.listeners.filter((l) => l !== cb));
  },
  emit() {
    this.listeners.forEach((cb) => cb());
  },
};

const CLAMP = 8;

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

const MoreButton = ({
  icon,
  children,
  imgWidth,
  imgHeight,
  title,
  placement = 'bottom-end',
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const computeCoords = () => {
    const btn = buttonRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;

    const b = btn.getBoundingClientRect();
    const m = menu.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;

    let left = 0;
    let top = 10;

    switch (placement) {
      case 'left':
        left = b.left + scrollX - m.width;
        top = b.top + scrollY;
        break;
      case 'right':
        left = b.right + scrollX;
        top = b.top + scrollY;
        break;
      case 'bottom-end':
        left = b.right + scrollX - m.width;
        top = b.bottom + scrollY;
        break;
      case 'bottom-start':
      default:
        left = b.left + scrollX;
        top = b.bottom + scrollY;
        break;
    }

    left = clamp(
      left,
      scrollX + CLAMP,
      scrollX + window.innerWidth - m.width - CLAMP
    );
    top = clamp(
      top,
      scrollY + CLAMP,
      scrollY + window.innerHeight - m.height - CLAMP
    );

    setCoords({ top, left });
  };

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (showMenu) {
      setShowMenu(false);
      return;
    }
    MoreButtonEventBus.emit();
    setShowMenu(true);
  };

  useEffect(() => {
    const unsubscribe = MoreButtonEventBus.subscribe(() => setShowMenu(false));
    const handleClickOutside = (e) => {
      if (
        !buttonRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setShowMenu(false);
      }
    };
    const handleScrollOrResize = () => showMenu && computeCoords();

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      unsubscribe();
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [showMenu]);

  useLayoutEffect(() => {
    if (showMenu) computeCoords();
  }, [showMenu, placement]);

  return (
    <div className={style.parent} ref={buttonRef}>
      <div className={style['title-container']} onClick={toggleMenu}>
        <SvgWithBranding
          type={icon}
          className={style['more-img']}
          style={{ width: imgWidth, height: imgHeight }}
        />
        {title && <h3>{title}</h3>}
      </div>

      {showMenu &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className={style['menu-container']}
            style={{
              top: coords.top + 10,
              left: coords.left,
              position: 'absolute',
              zIndex: 9999,
            }}
          >
            {React.isValidElement(children)
              ? React.cloneElement(children, {
                  onClose: () => setShowMenu(false),
                })
              : children}
          </div>,
          document.body
        )}
    </div>
  );
};

export default MoreButton;
